import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "../../lib/api";
import { useAdminData } from "../../hooks/useAdminData";
import { ReferralsPanel } from "../../components/admin/ReferralsPanel";
import { AdminSidebar, AdminMobileNav } from "../../components/admin/AdminNav";
import { OverviewPanel, RegistrationsPanel, GiftsPanel, ExportsPanel, AuditPanel } from "../../components/admin/AdminPanels";
import { SponsorshipsPanel } from "../../components/admin/SponsorshipsPanel";
import { RegistrationDetailModal } from "../../components/admin/RegistrationDetailModal";
import { ConfigEditor } from "../../components/admin/ConfigEditor";

// Runs an admin request with a success toast; returns the response data or undefined on error.
async function adminCall(request, successMsg) {
  try { const { data } = await request(); if (successMsg) toast.success(successMsg); return data ?? true; }
  catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return undefined; }
}

function useTabLoader(d, tab) {
  const { authed, loadStats, loadGifts, loadRegs, loadConfig, loadAudit, loadSpons } = d;
  useEffect(() => { if (authed) { loadStats(); loadGifts(); } }, [authed, loadStats, loadGifts]);
  useEffect(() => { if (authed && tab === "registrations") loadRegs(); }, [authed, tab, loadRegs]);
  useEffect(() => { if (authed && tab === "config") loadConfig(); }, [authed, tab, loadConfig]);
  useEffect(() => { if (authed && tab === "audit") loadAudit(); }, [authed, tab, loadAudit]);
  useEffect(() => { if (authed && tab === "sponsorships") loadSpons(); }, [authed, tab, loadSpons]);
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const d = useAdminData(navigate);
  const [tab, setTab] = useState("overview");
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  useTabLoader(d, tab);

  const { filters, setFilters } = d;
  const regFilters = {
    q, setQ,
    statusFilter: filters.status, setStatusFilter: (status) => setFilters((f) => ({ ...f, status })),
    refFilter: filters.referral, setRefFilter: (referral) => setFilters((f) => ({ ...f, referral })),
  };
  const search = () => setFilters((f) => ({ ...f, q }));

  const refreshAfterChange = () => { d.loadStats(); d.loadGifts(); d.loadRegs(); setSelected(null); };
  const confirmPayment = async (ref) => { if (await adminCall(() => api.post(`/admin/registrations/${ref}/confirm-payment`), "Payment confirmed")) refreshAfterChange(); };
  const cancelReg = async (ref) => {
    if (!window.confirm("Cancel this registration?")) return;
    if (await adminCall(() => api.post(`/admin/registrations/${ref}/cancel`), "Registration cancelled")) refreshAfterChange();
  };
  const deleteReg = async (ref) => {
    if (!window.confirm(`Permanently delete registration ${ref}? This cannot be undone.`)) return;
    if (await adminCall(() => api.delete(`/admin/registrations/${ref}`), "Registration deleted")) refreshAfterChange();
  };
  const sponsorAction = async (ref, path, body) => {
    const data = await adminCall(() => api.post(`/admin/sponsorships/${ref}/${path}`, body || {}), "Done");
    if (data) d.loadSpons();
    return data;
  };
  const exportCsv = (kind) => { window.open(`${API}/admin/export/${kind}`, "_blank"); };
  const logout = async () => { await api.post("/admin/logout"); navigate("/admin"); };
  const viewReferral = (code) => { setQ(""); setFilters({ q: "", status: "", referral: code }); setTab("registrations"); };

  if (d.authed === null) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#F8FAF8] md:flex">
      <AdminSidebar tab={tab} setTab={setTab} onLogout={logout} />
      <AdminMobileNav tab={tab} setTab={setTab} onLogout={logout} />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden" data-testid="admin-dashboard">
        {tab === "overview" && d.stats && <OverviewPanel stats={d.stats} />}
        {tab === "registrations" && <RegistrationsPanel regs={d.regs} filters={regFilters} onSearch={search} onView={setSelected} onDelete={deleteReg} />}
        {tab === "gifts" && d.gifts && <GiftsPanel gifts={d.gifts} />}
        {tab === "sponsorships" && d.spons && <SponsorshipsPanel spons={d.spons} onAction={sponsorAction} onExport={exportCsv} />}
        {tab === "referrals" && <ReferralsPanel onViewRegistrations={viewReferral} />}
        {tab === "config" && d.config && <ConfigEditor config={d.config} onSaved={(c) => { d.setConfig(c); toast.success("Configuration saved"); }} />}
        {tab === "exports" && <ExportsPanel onExport={exportCsv} />}
        {tab === "audit" && <AuditPanel audit={d.audit} />}
      </main>
      {selected && <RegistrationDetailModal selected={selected} onClose={() => setSelected(null)} onConfirm={confirmPayment} onCancel={cancelReg} onDelete={deleteReg} />}
    </div>
  );
}
